import { useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, AlertCircle, Check } from 'lucide-react';
import { api, APIError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/dialog';
import { useToast } from '@/components/shared';
import type { Definition } from './types';
export const dateLabel = (value: string | null, zone?: string) => value ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short', ...(zone ? { timeZone: zone } : {}) }).format(new Date(value)) : 'Not yet';
export const localInput = (date = new Date()) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
export const optionName = (options: Definition[], id: string | null) => options.find(o => o.id === id)?.name ?? 'Not set';
export function Field({ label, children, hint }: {
    label: string;
    children: ReactNode;
    hint?: string;
}) { return <label className="field-label">{label}{children}{hint && <small className="crm-hint">{hint}</small>}</label>; }
export function useCommand(onChange: () => void) {
    const [busy, setBusy] = useState(false), [error, setError] = useState<Error | null>(null);
    const toast = useToast();
    async function run<T>(path: string, body: unknown, method = 'POST', message = 'Changes saved.'): Promise<T | undefined> { setBusy(true); setError(null); try {
        const result = await api<T>(path, { method, body });
        onChange();
        toast(message);
        return result;
    }
    catch (e) {
        setError(e as Error);
        toast((e as Error).message, true);
        return undefined;
    }
    finally {
        setBusy(false);
    } }
    return { busy, error, run, clearError: () => setError(null) };
}
export function CommandError({ error, onOpen }: {
    error: Error | null;
    onOpen?: (id: string) => void;
}) {
    if (!error)
        return null;
    const details = error instanceof APIError ? error.details : undefined;
    const dup = (details?.duplicates ?? []) as {
        id: string;
        name: string;
        matches: string[];
        archived_at: string | null;
    }[];
    return <div className="crm-error" role="alert"><AlertCircle size={17}/><div><strong>{error.message}</strong>{dup.map(d => <button type="button" key={d.id} onClick={() => onOpen?.(d.id)} disabled={!onOpen}>Open {d.name} · {d.matches.join(' & ')} match{d.archived_at ? ' · Archived' : ''}</button>)}</div></div>;
}
export function Pagination({ page, total, hasMore, onChange }: {
    page: number;
    total: number;
    hasMore: boolean;
    onChange: (p: number) => void;
}) { return <div className="table-pagination"><span>{total} result{total === 1 ? '' : 's'} · Page {page + 1}</span><div><Button variant="outline" size="sm" disabled={!page} onClick={() => onChange(page - 1)}><ChevronLeft size={14}/>Previous</Button><Button variant="outline" size="sm" disabled={!hasMore} onClick={() => onChange(page + 1)}>Next<ChevronRight size={14}/></Button></div></div>; }
export function ConfirmAction({ title, description, label = 'Confirm', danger = false, onConfirm, onClose }: {
    title: string;
    description: string;
    label?: string;
    danger?: boolean;
    onConfirm: () => Promise<boolean>;
    onClose: () => void;
}) {
    const [busy, setBusy] = useState(false);
    return <Modal open onOpenChange={v => { if (!v && !busy)
        onClose(); }} title={title} description={description}><div className="modal-actions"><Button variant="outline" disabled={busy} onClick={onClose}>Keep unchanged</Button><Button variant={danger ? 'destructive' : 'default'} disabled={busy} onClick={async () => { setBusy(true); try {
        if (await onConfirm())
            onClose();
    }
    finally {
        setBusy(false);
    } }}>{busy ? 'Saving…' : label}</Button></div></Modal>;
}
export function StatusPill({ children, tone = 'neutral' }: {
    children: ReactNode;
    tone?: string;
}) { return <span className={`crm-pill crm-pill-${tone}`}><span />{children}</span>; }
export function SavedHint() { return <span className="crm-saved"><Check size={13}/>Saved in SQLite</span>; }
